/* ================================================================
   Niimbot Wishlist — client-side wishlist engine
   Storage: localStorage key `shopify_wishlist`
     [{ id, handle, title, price (cents), image, url }]
   Public API: window.NbWishlist
   Events dispatched on document:
     'wishlist:changed'  detail: { items }
   ================================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'shopify_wishlist';
  var EVENT_CHANGED = 'wishlist:changed';

  /* ── Storage ───────────────────────────────────────────── */
  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function write(items) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) {}
  }

  /* ── Core state ────────────────────────────────────────── */
  var state = { items: read() };

  function emitChanged() {
    document.dispatchEvent(new CustomEvent(EVENT_CHANGED, { detail: { items: state.items.slice() } }));
  }

  function getById(id) {
    return state.items.find(function (it) { return String(it.id) === String(id); }) || null;
  }

  function has(id) {
    return getById(id) !== null;
  }

  function add(product) {
    if (!product || !product.id) return false;
    if (has(product.id)) return false;
    state.items.push({
      id: product.id,
      handle: product.handle || '',
      title: product.title || '',
      price: typeof product.price === 'number' ? product.price : parseInt(product.price, 10) || 0,
      image: product.image || '',
      url: product.url || (product.handle ? '/products/' + product.handle : '')
    });
    write(state.items);
    emitChanged();
    return true;
  }

  function remove(id) {
    var idx = state.items.findIndex(function (it) { return String(it.id) === String(id); });
    if (idx === -1) return false;
    state.items.splice(idx, 1);
    write(state.items);
    emitChanged();
    return true;
  }

  function toggle(product) {
    if (!product || !product.id) return null;
    if (has(product.id)) { remove(product.id); return 'removed'; }
    add(product); return 'added';
  }

  function clear() {
    state.items = [];
    write(state.items);
    emitChanged();
  }

  /* ── Header badge ──────────────────────────────────────── */
  function updateHeaderBadge() {
    var count = state.items.length;
    document.querySelectorAll('[data-nb-wishlist-count]').forEach(function (el) {
      el.textContent = count;
    });
    document.querySelectorAll('[data-nb-wishlist-bubble]').forEach(function (el) {
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
    // Toggle filled vs outlined heart on the header icon based on count.
    // Supports both the Dawn header (.header__icon--wishlist) and the
    // custom Niimbot header (.nb-header__wishlist).
    document.querySelectorAll('.header__icon--wishlist, .nb-header__wishlist').forEach(function (el) {
      el.classList.toggle('is-filled', count > 0);
    });
  }

  /* ── PDP button state sync ─────────────────────────────── */
  function syncPdpButtons() {
    document.querySelectorAll('[data-nb-wishlist-toggle]').forEach(function (btn) {
      var id = btn.getAttribute('data-product-id');
      var on = has(id);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('is-active', on);
      var label = btn.querySelector('[data-nb-wishlist-label]');
      if (label) label.textContent = on ? (btn.dataset.activeLabel || 'In Your Wishlist') : (btn.dataset.defaultLabel || 'Add to Wishlist');
      // Icon-only buttons just toggle aria-label / title.
      if (btn.classList.contains('nb-wishlist-icon')) {
        var msg = on ? 'In your wishlist' : 'Add to wishlist';
        btn.setAttribute('aria-label', msg);
        btn.setAttribute('title', msg);
      }
    });
  }

  /* ── Toast (lightweight; reused for add / remove confirmation) ── */
  function toast(msg) {
    var t = document.createElement('div');
    t.className = 'nb-wishlist-toast';
    t.textContent = msg;
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('is-visible'); });
    setTimeout(function () {
      t.classList.remove('is-visible');
      setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 240);
    }, 2200);
  }

  /* ── Read product data off a PDP toggle button ─────────── */
  function productDataFromButton(btn) {
    return {
      id: btn.getAttribute('data-product-id'),
      handle: btn.getAttribute('data-product-handle') || '',
      title: btn.getAttribute('data-product-title') || '',
      price: parseInt(btn.getAttribute('data-product-price'), 10) || 0,
      image: btn.getAttribute('data-product-image') || '',
      url: btn.getAttribute('data-product-url') || ''
    };
  }

  /* ── Click bindings ────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    /* PDP / generic toggle */
    var toggleBtn = e.target.closest('[data-nb-wishlist-toggle]');
    if (toggleBtn) {
      e.preventDefault();
      var product = productDataFromButton(toggleBtn);
      var result = toggle(product);
      toast(result === 'added' ? 'Added to your wishlist' : 'Removed from your wishlist');
      return;
    }

    /* "Move to Wishlist" from a cart line */
    var moveBtn = e.target.closest('[data-nb-wishlist-move]');
    if (moveBtn) {
      e.preventDefault();
      var product2 = productDataFromButton(moveBtn);
      var wasNew = add(product2);
      toast(wasNew ? 'Moved to your wishlist' : 'Already in your wishlist');
      var key = moveBtn.getAttribute('data-key');
      var row = moveBtn.closest('.nb-cart__item, .nb-cartpage__row');
      var removeBtn = row && row.querySelector('[data-nb-remove]');
      if (removeBtn && removeBtn.click) removeBtn.click();
      else if (key && window.NbCart && typeof window.NbCart.refresh === 'function') {
        // Fallback: direct /cart/change.js
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        }).then(function () { window.NbCart.refresh(); });
      }
      return;
    }

    /* Remove from the wishlist page card */
    var pageRemove = e.target.closest('[data-nb-wishlist-remove]');
    if (pageRemove) {
      e.preventDefault();
      var rid = pageRemove.getAttribute('data-product-id');
      var card = pageRemove.closest('.nb-wishlist__card');
      remove(rid);
      if (card) {
        card.classList.add('is-removing');
        setTimeout(function () { if (card.parentNode) card.parentNode.removeChild(card); refreshEmptyState(); }, 220);
      } else {
        refreshEmptyState();
      }
      return;
    }

    /* Wishlist page: Add to Cart (single-variant fast path) */
    var addBtn = e.target.closest('[data-nb-wishlist-add]');
    if (addBtn) {
      e.preventDefault();
      if (addBtn.disabled) return;
      var variantId = addBtn.getAttribute('data-variant-id');
      if (!variantId) return;
      addBtn.disabled = true;
      var originalText = addBtn.textContent;
      addBtn.textContent = 'Adding…';
      var addUrl = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('Add to cart failed');
          if (window.NbCart) { return window.NbCart.refresh().then(function () { window.NbCart.open && window.NbCart.open(); }); }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        })
        .then(function () {
          addBtn.textContent = 'Added ✓';
          setTimeout(function () { addBtn.disabled = false; addBtn.textContent = originalText; }, 1800);
        })
        .catch(function () {
          addBtn.disabled = false;
          addBtn.textContent = originalText;
          toast('Could not add to cart');
        });
      return;
    }
  });

  /* ── Wishlist page rendering ───────────────────────────── */
  function fmtMoneyCents(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    return 'S$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function refreshEmptyState() {
    var grid = document.querySelector('[data-nb-wishlist-grid]');
    if (!grid) return;
    var empty = document.querySelector('[data-nb-wishlist-empty]');
    if (state.items.length === 0) {
      if (empty) empty.removeAttribute('hidden');
      grid.setAttribute('hidden', '');
    } else {
      if (empty) empty.setAttribute('hidden', '');
      grid.removeAttribute('hidden');
    }
  }

  /**
   * Fetch fresh product data from /products/<handle>.js for each saved item.
   * If the product is gone (404) we silently prune it from the wishlist so
   * shoppers don't stare at broken cards.
   */
  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function renderCard(saved, product) {
    var hasManyVariants = product && product.variants && product.variants.length > 1;
    var firstAvailable = product && (product.variants || []).find(function (v) { return v.available; });
    var disabled = !product || !firstAvailable;
    var compareCents = product && product.compare_at_price ? product.compare_at_price : 0;
    var priceCents = product && typeof product.price === 'number' ? product.price : saved.price;
    var onSale = compareCents > 0 && compareCents > priceCents;
    var img = (product && product.featured_image) || saved.image || '';
    var url = (product && product.url) || saved.url || ('/products/' + saved.handle);
    var title = (product && product.title) || saved.title;

    var actionHtml;
    if (disabled) {
      actionHtml = '<button type="button" class="nb-wishlist__action" disabled>Sold out</button>';
    } else if (hasManyVariants) {
      actionHtml =
        '<a href="' + escAttr(url) + '" class="nb-wishlist__action nb-wishlist__action--link">Choose options' +
        ' <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/></svg>' +
        '</a>';
    } else {
      actionHtml =
        '<button type="button" class="nb-wishlist__action" data-nb-wishlist-add data-variant-id="' + escAttr(firstAvailable.id) + '">Add to cart' +
        ' <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/></svg>' +
        '</button>';
    }

    return (
      '<article class="nb-wishlist__card">' +
        '<button type="button" class="nb-wishlist__remove" data-nb-wishlist-remove data-product-id="' + escAttr(saved.id) + '" aria-label="Remove from wishlist" title="Remove from wishlist">' +
          '<svg viewBox="0 0 18 18" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="3" y1="3" x2="15" y2="15"/><line x1="15" y1="3" x2="3" y2="15"/></svg>' +
        '</button>' +
        (onSale ? '<span class="nb-wishlist__badge">Sale</span>' : '') +
        '<a href="' + escAttr(url) + '" class="nb-wishlist__media" aria-label="' + escAttr(title) + '">' +
          (img ? '<img src="' + escAttr(img) + '" alt="' + escAttr(title) + '" loading="lazy">' : '<div class="nb-wishlist__placeholder" aria-hidden="true"></div>') +
        '</a>' +
        '<a href="' + escAttr(url) + '" class="nb-wishlist__title">' + escHtml(title) + '</a>' +
        '<div class="nb-wishlist__prices">' +
          '<span class="nb-wishlist__price">' + fmtMoneyCents(priceCents) + '</span>' +
          (onSale ? ' <s class="nb-wishlist__compare">' + fmtMoneyCents(compareCents) + '</s>' : '') +
        '</div>' +
        actionHtml +
      '</article>'
    );
  }

  function renderWishlistPage() {
    var grid = document.querySelector('[data-nb-wishlist-grid]');
    if (!grid) return;

    refreshEmptyState();
    if (state.items.length === 0) return;

    // Skeleton placeholders until products resolve.
    grid.innerHTML = state.items
      .map(function () { return '<article class="nb-wishlist__card nb-wishlist__card--skeleton" aria-hidden="true"></article>'; })
      .join('');

    var savedSnapshot = state.items.slice();
    Promise.all(savedSnapshot.map(function (s) { return s.handle ? fetchProduct(s.handle) : Promise.resolve(null); }))
      .then(function (products) {
        // Prune saved items whose products no longer resolve (deleted/unpublished).
        var keep = [];
        var html = '';
        savedSnapshot.forEach(function (s, i) {
          var p = products[i];
          if (p === null && s.handle) {
            // 404 → drop from wishlist
            return;
          }
          keep.push(s);
          html += renderCard(s, p);
        });
        if (keep.length !== state.items.length) {
          state.items = keep;
          write(state.items);
          emitChanged();
        }
        grid.innerHTML = html;
        refreshEmptyState();
      });
  }

  /* ── Small HTML helpers ────────────────────────────────── */
  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escAttr(s) { return escHtml(s); }

  /* ── Cross-tab sync ────────────────────────────────────── */
  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    state.items = read();
    updateHeaderBadge();
    syncPdpButtons();
    refreshEmptyState();
  });

  /* ── Event hooks ──────────────────────────────────────── */
  document.addEventListener(EVENT_CHANGED, function () {
    updateHeaderBadge();
    syncPdpButtons();
  });

  /* ── Init ─────────────────────────────────────────────── */
  function init() {
    updateHeaderBadge();
    syncPdpButtons();
    if (window.location.pathname.indexOf('/pages/wishlist') !== -1 ||
        document.querySelector('[data-nb-wishlist-grid]')) {
      renderWishlistPage();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Public API ───────────────────────────────────────── */
  window.NbWishlist = {
    items: function () { return state.items.slice(); },
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clear
  };
})();
